import type { KeyPair } from "@foreman/crypto";
import { RemoteClient, type ChatMessage, type Connector, type OfficeReply } from "@foreman/link";
import { ChatStore } from "./chat";
import type { GitDiffReply, GitReply } from "@foreman/link";
import { GIT_REF_RE, GitOp as GitOpSchema, type AgentUsage, type ApprovalRule, type PowerSettings, type PowerState, toBase64, type DeviceInfo, type GitOp, type FolderEntry, HistoryEntry, InboxEvent, SessionInfo, type SkillInfo, type SkillOp, type CatalogSkill, TranscriptItem } from "@foreman/protocol";
import { publicKeyOf, type ComputerRecord } from "./computers";
import { samePowerSettings, type PowerPrefs } from "./power";
import { addActivity, addMessage, mergeMessages, type ActivityBuffer, type OfficeSnapshot } from "./office";
import type { OfficeActivity, OfficeMessage, OfficeSummary } from "@foreman/protocol";

/**
 * What the UI shows for a computer.
 *  connecting    first attempt, never connected yet
 *  ready         connected
 *  reconnecting  was connected (or the network failed); retrying automatically
 *  offline       the relay is fine but the computer's daemon is not connected
 *  unauthorized  this phone was removed from the computer (permanent)
 */
export type CheckResult = "online" | "offline" | "unknown";
export type ConnState = "connecting" | "ready" | "reconnecting" | "offline" | "unauthorized";

export interface ConnectionSnapshot {
  state: ConnState;
  /** Newest first. */
  sessions: SessionInfo[];
  /** "loading" only before the first list arrives; later refreshes are "refreshing". */
  sessionsStatus: "loading" | "ready" | "refreshing";
  sessionsUpdatedAt: number | null;
  devices: DeviceInfo[];
  devicesStatus: "idle" | "loading" | "ready" | "error";
  /** What this computer's agents need or reported, newest first. */
  inbox: InboxEvent[];
  /** False until the computer has sent its inbox at least once. */
  inboxLoaded: boolean;
  /** The computer has been offline for a long while, so retrying stopped. `reconnectNow()` (or the app coming to the front) starts again. */
  paused: boolean;
  /** Whether the computer is signed in to an account, with a masked address; null until it has said (or if it is an older version). */
  account: { signedIn: boolean; hint: string | null; plan?: "guest" | "free" | "pro" } | null;
  /** Why the relay turned this phone away, when it did for a reason a sign-in fixes (a guest's second computer). */
  blocked: "guest_computer" | null;
  /** What the computer itself says about how it is kept reachable (null until it has said, or for an older computer). Only ever what the computer confirmed. */
  power: PowerState | null;
  /** The computer is an older Brocoded that cannot hold itself awake. */
  powerUnsupported: boolean;
  /** The computer refused or could not apply the last change. */
  powerError: string | null;
  /** How much of each agent's plan limits is used, as this computer last read it; null until it has said. `usageUnsupported`: an older Brocoded. */
  usage?: { agents: AgentUsage[]; at: number } | null;
  usageUnsupported?: boolean;
  /** A reading is under way (it can take several seconds). */
  usageLoading?: boolean;
  /** The approval rules kept on this computer; null until it has said. `rulesUnsupported`: an older Brocoded that cannot keep them. */
  rules?: ApprovalRule[] | null;
  rulesUnsupported?: boolean;
  /** The team on this computer (its agents and tasks); null until it has said. `officeUnsupported`: an older Brocoded that has no teams. */
  office?: OfficeSnapshot | null;
  officeUnsupported?: boolean;
  /** What each agent did lately, newest first, kept up to date as it happens. Older entries come from `officeHistory`. */
  officeActivity?: ActivityBuffer;
  /** What the team passed on (hand-overs, answers to the person), newest first, kept up to date as it happens. */
  officeMessages?: OfficeMessage[];
  /** The most recent message that arrived LIVE (not from the history fetch): the world animates a hand-over from it. */
  officeLive?: OfficeMessage | null;
  /** Whether this computer can turn a recording into text; null until `refreshVoice` has been answered. */
  voice?: VoiceStatus | null;
  /** When the link looked up but the computer stopped answering and was redialled; null if it never happened. Reported once per occurrence. */
  staleAt?: number | null;
  /** A "check now" is in progress (so the button can show it); see `checkNow`. */
  checking?: boolean;
}

export interface VoiceStatus {
  ready: boolean;
  /** Why not, in words that say what to do (from the computer, or "update Brocoded" for an older one). */
  reason?: string;
  /** The longest recording it takes, in seconds. */
  limitSeconds?: number;
  /** The computer is an older Brocoded that has no voice at all. */
  unsupported?: boolean;
  /** What is missing: `model` can be fetched from the phone (`setupVoice`); `program` can't. */
  needs?: "model" | "program";
  /** "Set up voice" is running on the computer (this phone's or another's). */
  settingUp?: boolean;
  /** Where "Set up voice" is, or why it failed. Cleared when voice is ready. */
  setup?: { stage: "installing" | "downloading" | "checking" | "done" | "error"; percent?: number; message?: string } | null;
}

export interface SkillsReply {
  ok: boolean;
  message?: string;
  skills: SkillInfo[];
  catalog?: CatalogSkill[];
  library: string;
}

export interface FolderListing {
  path: string;
  parent: string | null;
  dirs: FolderEntry[];
  truncated: boolean;
  /** Recent projects: only in the home listing (when no path was asked for). */
  recent?: FolderEntry[];
  /** Common places (Home, Desktop, Documents, ...): only in the home listing. */
  places?: FolderEntry[];
}

export interface FolderSearch {
  root: string;
  results: FolderEntry[];
  truncated: boolean;
}

export type InboxAction = "approve" | "deny" | "answer";

const byNewest = (a: InboxEvent, b: InboxEvent) => b.createdAt - a.createdAt;

/** How many items need a decision (approvals and questions still open) and how many are notices (finished/failed). */
export function inboxCounts(events: InboxEvent[]): { needsYou: number; notices: number } {
  let needsYou = 0;
  let notices = 0;
  for (const e of events) {
    if (e.state !== "open") continue;
    if (e.kind === "approval" || e.kind === "question") needsYou++;
    else notices++;
  }
  return { needsYou, notices };
}

export interface SessionListener {
  onOutput(offset: number, data: Uint8Array): void;
  /** Output between `expected` and `actual` was no longer available on the computer. */
  onGap(expected: number, actual: number): void;
  /** `exitCode` is null when the process was terminated by a signal. */
  onExit(exitCode: number | null): void;
  onError(error: { code: string; message: string }): void;
}

/** What Home needs to draw itself before the computer has answered: the session list and the team, as last heard. */
export interface HomeCacheEntry {
  sessions: SessionInfo[];
  office: OfficeSnapshot | null;
  /** When the session list was last confirmed by the computer (not when it was saved), so "Updated 09:41" stays true. */
  savedAt: number;
}

export interface ConnectionOptions {
  keys: KeyPair;
  connectorFor: (relayUrl: string, daemonPublicKey: Uint8Array) => Connector;
  backoff?: { minMs: number; maxMs: number };
  handshakeTimeoutMs?: number;
  /** How long to wait for the computer to answer a request before giving up (ms). */
  requestTimeoutMs?: number;
  /** A "ready" link that has heard nothing for this long is probed (ms, default 20 s); no answer within `probeGraceMs` (default 8 s) and it is redialled. */
  quietProbeMs?: number;
  probeGraceMs?: number;
  /** How often the link is checked (ms, default 4 s). */
  watchdogTickMs?: number;
  /** While the app is in the background the link is probed this much less often (ms, default 90 s). */
  backgroundProbeMs?: number;
  now?: () => number;
  /** How long a computer gets to come back online after a wake packet was sent (ms, default 90 s). Used by the hub. */
  wakeWaitMs?: number;
  /** Availability choices waiting to reach a computer, and where to wake it (kept on this phone). Without it nothing is remembered. */
  power?: PowerPrefs;
  /**
   * Remembers what each computer last said about this phone's account, so a signed-in phone opens already showing its plan
   * instead of the guest "sign in" cards while it reconnects. Only a signed-in answer is ever saved.
   */
  accountCache?: { load(computerId: string): ConnectionSnapshot["account"]; save(computerId: string, account: NonNullable<ConnectionSnapshot["account"]>): void };
  /**
   * Keeps what Home shows on this phone. The app opens already showing the last list (as a refresh, not a spinner), and the
   * real answer replaces it quietly when it arrives. Decisions waiting on the person (approvals, questions) are never kept:
   * a stale card would be answered for something that has already moved on.
   */
  homeCache?: { load(computerId: string): HomeCacheEntry | null; save(computerId: string, entry: HomeCacheEntry): void };
}

/** How long a guest answer is held back while a signed-in phone signs its computer in (ms). */
const ATTACH_GRACE_MS = 20_000;

/** An error from the computer, keeping its code and feature so the app can tell "needs a sign-in" from a failure. */
const errOf = (e: { code: string; message: string; feature?: string }): Error => Object.assign(new Error(e.message), { code: e.code, ...(e.feature ? { feature: e.feature } : {}) });

/** A page of a conversation. `end`: ask for `after` this to follow it. `local`: a chat open in a terminal on the computer. */
export interface TranscriptReply {
  items: TranscriptItem[];
  before: number | null;
  end?: number;
  local?: "idle" | "busy";
  reset?: boolean;
}

/** After connecting, errors that arrive this soon answer what the app itself asked for, not something the person did. */
const BACKGROUND_REQUESTS_MS = 4000;

interface Pending<T> {
  resolve: (v: T) => void;
  reject: (e: Error) => void;
  timer: ReturnType<typeof setTimeout>;
}

/**
 * Everything the UI needs from one paired computer, as a small observable store:
 * connection state, the (pull-based) session list, session creation, device management, and
 * attaching a terminal to a session. One RemoteClient underneath keeps the encrypted connection
 * alive and replays missed output.
 */
/** Optional extras for a push address: Web Push keys, and whether alerts may say which session it is. */
export interface PushExtra {
  keys?: { p256dh: string; auth: string };
  details?: boolean;
  /** `false`: no alert when an agent finishes. */
  finished?: boolean;
  /** Quiet hours, in this phone's time zone. */
  quiet?: { from: number; to: number; tz: string };
}

export class ComputerConnection {
  private snap: ConnectionSnapshot = {
    state: "connecting",
    sessions: [],
    sessionsStatus: "loading",
    sessionsUpdatedAt: null,
    devices: [],
    devicesStatus: "idle",
    inbox: [],
    inboxLoaded: false,
    paused: false,
    account: null,
    blocked: null,
    power: null,
    powerUnsupported: false,
    powerError: null,
    voice: null,
  };
  private readonly listeners = new Set<() => void>();
  private readonly client: RemoteClient;
  private everReady = false;
  /** When the connection last became ready: requests the app itself sends right then (account, push address) are not the person's. */
  private readyAt = 0;
  private readonly terminals = new Map<string, SessionListener>();
  private readonly exits = new Map<string, number | null>();
  private creating: Pending<SessionInfo> | null = null;
  private revoking: Pending<void> | null = null;
  private readonly responding = new Map<string, Pending<void>>();
  /**
   * Folder listings, one at a time: the computer answers each as it finishes, so with two in flight a reply could not
   * be told from the other. The first in the queue is the one that has been sent.
   */
  private readonly historyRequests = new Map<string, { promise: Promise<HistoryEntry[]>; resolve(v: HistoryEntry[]): void; reject(e: Error): void; timer: ReturnType<typeof setTimeout> }>();
  private readonly transcriptRequests = new Map<string, Pending<TranscriptReply>>();
  /** Keyed `sessionId\0against`: a comparison and the plain list for the same session are different questions. */
  private readonly diffRequests = new Map<string, Pending<GitDiffReply> & { promise: Promise<GitDiffReply> }>();
  /** Branches / log / commit / git changes, each matched to its answer by the `rid` it was sent with. */
  private readonly gitCalls = new Map<string, Pending<GitReply> & { sessionId: string }>();
  private gitSeq = 0;
  private findSeq = 0;
  private readonly fileCalls = new Map<string, { resolve(r: { results: string[]; truncated: boolean }): void; timer: ReturnType<typeof setTimeout> }>();
  private fileSeq = 0;
  private readonly skillCalls = new Map<string, { resolve(r: SkillsReply): void; timer: ReturnType<typeof setTimeout> }>();
  private skillSeq = 0;
  private officeSeq = 0;
  private officeAsked: ReturnType<typeof setTimeout> | null = null;
  private readonly officeCalls = new Map<string, { resolve(r: OfficeReply): void; timer: ReturnType<typeof setTimeout> }>();
  private voiceAsked = false;
  private rulesAsked = false;
  private usageAsked = false;
  private ruleError: ((message: string) => void) | null = null;
  private voiceSeq = 0;
  private imageSeq = 0;
  private readonly imageCalls = new Map<string, { resolve(path: string): void; reject(e: Error): void; timer: ReturnType<typeof setTimeout> }>();
  private readonly voiceCalls = new Map<string, { resolve(text: string): void; reject(e: Error): void; timer: ReturnType<typeof setTimeout> }>();
  private readonly findCalls = new Map<string, { resolve(r: FolderSearch): void; reject(e: Error): void; timer: ReturnType<typeof setTimeout> }>();
  private readonly folderRequests: (Pending<FolderListing> & { path: string | undefined; sent: boolean })[] = [];
  private devicesTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly requestTimeoutMs: number;
  private readonly now: () => number;

  constructor(
    readonly computer: ComputerRecord,
    private readonly opts: ConnectionOptions,
  ) {
    this.requestTimeoutMs = opts.requestTimeoutMs ?? 10_000;
    this.now = opts.now ?? Date.now;
    const cached = opts.accountCache?.load(computer.id);
    if (cached) this.snap = { ...this.snap, account: cached };
    const home = opts.homeCache?.load(computer.id);
    if (home && home.sessions.length > 0) {
      // shown as a refresh in progress: no skeletons, and what arrives next just replaces it
      this.snap = { ...this.snap, sessions: sort(home.sessions), sessionsStatus: "refreshing", sessionsUpdatedAt: home.savedAt, ...(home.office ? { office: home.office } : {}) };
    } else if (home?.office) {
      this.snap = { ...this.snap, office: home.office };
    }
    const daemonKey = publicKeyOf(computer);
    this.client = new RemoteClient({
      keys: opts.keys,
      daemonPublicKey: daemonKey,
      connect: opts.connectorFor(computer.relayUrl, daemonKey),
      backoff: opts.backoff,
      handshakeTimeoutMs: opts.handshakeTimeoutMs,
      events: {
        onState: (s, detail) => this.onClientState(s, detail),
        onSessions: (sessions) => {
          this.set({ sessions: sort(sessions), sessionsStatus: "ready", sessionsUpdatedAt: this.now() });
          this.persistHome();
          // the list is the computer's word on which way each session runs
          for (const s of sessions) {
            const store = this.chats.get(s.id);
            const view = store?.getSnapshot();
            if (store && view && !view.switching && view.mode !== (s.mode ?? "terminal")) store.patch({ mode: s.mode ?? "terminal", loaded: false });
          }
        },
        onSessionCreated: (s) => this.onCreated(s),
        onOutput: (id, offset, data) => this.terminals.get(id)?.onOutput(offset, data),
        onGap: (id, expected, actual) => this.terminals.get(id)?.onGap(expected, actual),
        onExit: (id, code) => this.onExit(id, code),
        onFolders: (listing) => {
          const p = this.folderRequests.shift();
          if (p) {
            clearTimeout(p.timer);
            p.resolve(listing);
          }
          this.pumpFolders();
        },
        onHistory: (cwd, items) => {
          const p = this.historyRequests.get(cwd);
          if (!p) return;
          this.historyRequests.delete(cwd);
          clearTimeout(p.timer);
          p.resolve(items);
        },
        onTranscript: (sessionId, items, before, info) => {
          const p = this.transcriptRequests.get(sessionId);
          if (!p) return;
          this.transcriptRequests.delete(sessionId);
          clearTimeout(p.timer);
          p.resolve({ items, before, ...info });
        },
        onGitDiff: (result) => {
          const key = diffKey(result.sessionId, result.against);
          const p = this.diffRequests.get(key);
          if (!p) return;
          this.diffRequests.delete(key);
          clearTimeout(p.timer);
          p.resolve(result);
        },
        onSkills: (reply) => {
          const p = this.skillCalls.get(reply.rid);
          if (!p) return;
          this.skillCalls.delete(reply.rid);
          clearTimeout(p.timer);
          p.resolve({ ok: reply.ok, message: reply.message, skills: reply.skills, catalog: reply.catalog, library: reply.library });
        },
        onFilesFound: (found) => {
          const p = this.fileCalls.get(found.rid);
          if (!p) return;
          this.fileCalls.delete(found.rid);
          clearTimeout(p.timer);
          p.resolve({ results: found.results, truncated: found.truncated });
        },
        onFolderFound: (found) => {
          const p = this.findCalls.get(found.rid);
          if (!p) return; // a search the person has already moved on from
          this.findCalls.delete(found.rid);
          clearTimeout(p.timer);
          if (found.error !== undefined) p.reject(Object.assign(new Error(found.error), { denied: !!found.denied }));
          else p.resolve({ root: found.root, results: found.results, truncated: found.truncated });
        },
        onGitReply: (reply) => {
          const p = this.gitCalls.get(reply.rid);
          if (!p) return;
          this.gitCalls.delete(reply.rid);
          clearTimeout(p.timer);
          p.resolve(reply);
        },
        onInbox: (events) => this.set({ inbox: [...events].sort(byNewest), inboxLoaded: true }),
        onInboxEvent: (event) => this.onInboxEvent(event),
        onUsage: (incoming) => {
          const complete = incoming.complete !== false;
          const prior = this.snap.usage;
          const agents = new Map((prior?.agents ?? []).map((a) => [a.agent, a]));
          for (const agent of incoming.agents) agents.set(agent.agent, agent);
          const usage = { agents: [...agents.values()], at: incoming.at };
          if (complete) this.usageAsked = false;
          this.set({ usage, usageLoading: !complete, usageUnsupported: false });
        },
        onOffice: (reply) => this.onOffice(reply),
        onRules: (rules) => {
          this.rulesAsked = false;
          this.set({ rules, rulesUnsupported: false });
        },
        onInboxRemoved: (id) => this.set({ inbox: this.snap.inbox.filter((e) => e.id !== id) }),
        onDevices: (devices) => this.onDevices(devices),
        onAccountState: (account) => this.onAccountState(account),
        onPowerState: (state) => this.onPowerState(state),
        onVoiceState: (v) => {
          this.voiceAsked = false;
          const keep = this.snap.voice?.setup;
          // the computer says where setup stands: a finished or running one is shown, a stale "done" or failure is dropped once voice is ready
          this.set({ voice: { ...v, ...(keep && !v.ready ? { setup: keep } : {}) } });
        },
        onVoiceSetup: (p) => {
          const voice = this.snap.voice ?? { ready: false };
          this.set({ voice: { ...voice, setup: p, settingUp: p.stage === "installing" || p.stage === "downloading" || p.stage === "checking" } });
        },
        onImageSaved: (r) => {
          const p = this.imageCalls.get(r.rid);
          if (!p) return;
          this.imageCalls.delete(r.rid);
          clearTimeout(p.timer);
          if (r.ok && r.path) p.resolve(r.path);
          else p.reject(new Error(r.message ?? "Your computer couldn't save that picture."));
        },
        onVoiceTranscribed: (r) => {
          const p = this.voiceCalls.get(r.rid);
          if (!p) return;
          this.voiceCalls.delete(r.rid);
          clearTimeout(p.timer);
          if (r.ok && r.text !== undefined) p.resolve(r.text);
          else p.reject(new Error(r.message ?? "Your computer couldn't turn that into text."));
        },
        onWakeSent: (r) => {
          const p = this.wakeCall;
          this.wakeCall = null;
          if (p) (clearTimeout(p.timer), p.resolve(r));
        },
        onDeviceRevoked: (id) => this.onDeviceRevoked(id),
        onError: (e) => this.onError(e),
        onChat: (m) => this.onChat(m),
      },
    });
  }

  // ---- observable store ------------------------------------------------------------

  subscribe = (cb: () => void): (() => void) => {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  };
  getSnapshot = (): ConnectionSnapshot => this.snap;

  /** Set while a signed-in phone is signing its computer in: the computer's first answer is often still "guest", which would flash the sign-in cards. */
  private attaching: ReturnType<typeof setTimeout> | null = null;
  private heldGuest: NonNullable<ConnectionSnapshot["account"]> | null = null;

  /** What the computer last said this phone's plan is, on this connection (including answers held back from the screen). */
  private reportedPlan: string | null = null;

  private onAccountState(account: NonNullable<ConnectionSnapshot["account"]>): void {
    // The computer builds the inbox and the session list for the plan the phone has at the moment it asks. Right after a reconnect
    // that is "guest", until the sign-in on this connection goes through, so ask again once the plan improves.
    const before = this.reportedPlan;
    this.reportedPlan = account.plan ?? null;
    if (before === "guest" && account.plan && account.plan !== "guest" && this.client.ready) {
      this.client.listInbox();
      this.refreshSessions();
    }
    if (this.attaching && !account.signedIn) {
      this.heldGuest = account; // shown only if signing in doesn't work out within the grace time
      return;
    }
    this.releaseAttach(false);
    this.set({ account });
    if (account.signedIn) this.opts.accountCache?.save(this.computer.id, account);
  }

  private releaseAttach(apply: boolean): void {
    if (this.attaching) clearTimeout(this.attaching);
    this.attaching = null;
    const held = this.heldGuest;
    this.heldGuest = null;
    if (apply && held) this.set({ account: held });
  }

  private set(patch: Partial<ConnectionSnapshot>): void {
    this.snap = { ...this.snap, ...patch };
    for (const l of [...this.listeners]) l();
  }

  // ---- lifecycle -------------------------------------------------------------------

  start(): void {
    this.client.start();
    this.watchdog ??= setInterval(() => this.checkLink(), this.opts.watchdogTickMs ?? 4_000);
  }

  private background = false;
  /**
   * Light mode: the app is out of sight, so nobody is watching the dot. The link stays up (alerts still arrive) but a quiet
   * one is probed far less often, which is fewer messages through the relay for a phone that is just sitting there.
   */
  setBackground(background: boolean): void {
    this.background = background;
  }

  private watchdog: ReturnType<typeof setInterval> | null = null;
  private probeAt: number | null = null;
  private reprobed = false;

  /**
   * "Connected" must mean the computer answers, not just that our socket to the relay is open. After the computer loses its
   * network the relay can keep it listed for a while. If a ready link has been silent, ask it something small; if it still
   * says nothing, say so (state becomes reconnecting) and dial again instead of showing a green dot over a dead path.
   */
  private checkLink(): void {
    if (this.snap.state !== "ready") {
      this.probeAt = null;
      this.reprobed = false;
      return;
    }
    const t = this.now();
    const heard = Math.max(this.client.heardAt, this.readyAt ?? 0);
    if (this.probeAt !== null && this.client.heardAt >= this.probeAt) {
      this.probeAt = null; // it answered
      this.reprobed = false;
    }
    if (this.probeAt === null) {
      const quietMs = this.background ? (this.opts.backgroundProbeMs ?? 90_000) : (this.opts.quietProbeMs ?? 20_000);
      if (t - heard > quietMs) {
        this.probeAt = t;
        this.client.listSessions();
      }
      return;
    }
    const grace = this.opts.probeGraceMs ?? 14_000;
    if (!this.reprobed && t - this.probeAt > grace / 2) {
      // One slow answer (a busy computer, a weak signal) is not a dead link: ask once more before redialling.
      this.reprobed = true;
      this.client.listSessions();
    }
    if (t - this.probeAt > grace) {
      this.probeAt = null;
      this.reprobed = false;
      this.clearPending(new Error("Connection lost"));
      this.set({ state: "reconnecting", staleAt: t });
      this.client.reconnectNow();
    }
  }

  stop(): void {
    this.flushHome();
    if (this.watchdog) clearInterval(this.watchdog);
    this.watchdog = null;
    this.client.stop();
    this.clearPending(new Error("disconnected"));
  }

  /**
   * Ask the computer something small and time the answer: milliseconds, or null if nothing came back in time. Used by the
   * connection diagnostics; it proves the computer itself answers, not just that our socket to the relay is open.
   */
  probe(timeoutMs = 5_000): Promise<number | null> {
    if (!this.client.ready) return Promise.resolve(null);
    const t0 = Date.now();
    this.client.listSessions();
    return new Promise((resolve) => {
      const tick = setInterval(() => {
        if (this.client.heardAt >= t0) {
          clearInterval(tick);
          resolve(Math.max(1, Date.now() - t0));
        } else if (Date.now() - t0 > timeoutMs) {
          clearInterval(tick);
          resolve(null);
        }
      }, 50);
    });
  }

  private checkWaiter: ((r: CheckResult) => void) | null = null;
  private settleCheck(r: CheckResult): void {
    const w = this.checkWaiter;
    this.checkWaiter = null;
    w?.(r);
  }

  /**
   * The "Check now" button: reconnect right away and say what came of it. "online" (connected again), "offline" (the relay
   * says the computer still isn't connected) or "unknown" (no answer in time). The spinner shows for at least a moment, so a
   * tap on a computer that is still off visibly did something instead of looking dead.
   */
  checkNow(timeoutMs = 10_000): Promise<CheckResult> {
    if (this.snap.checking) return Promise.resolve("unknown");
    this.set({ checking: true });
    const started = this.now();
    const result = new Promise<CheckResult>((resolve) => {
      const timer = setTimeout(() => this.settleCheck("unknown"), timeoutMs);
      this.checkWaiter = (r) => {
        clearTimeout(timer);
        resolve(r);
      };
    });
    this.client.reconnectNow();
    return result.then(async (r) => {
      const shown = this.now() - started;
      if (shown < 800) await new Promise((res) => setTimeout(res, 800 - shown));
      this.set({ checking: false });
      return r;
    });
  }

  // ---- availability -----------------------------------------------------------------

  private powerAsked = false;
  private wakeCall: { resolve(r: { ok: boolean; message?: string }): void; timer: ReturnType<typeof setTimeout> } | null = null;

  /** Ask for the computer's power state; if the person chose something while it was away, send that instead (the answer is the confirmation). */
  private askPower(): void {
    const desired = this.opts.power?.get(this.computer.id).desired;
    this.powerAsked = true;
    if (desired) this.client.setPower(desired);
    else this.client.getPower();
  }

  /**
   * The person changed a setting. It is remembered here as pending and sent now if the computer is connected; it only counts as
   * applied when the computer answers with its own state. A computer that is away gets it as soon as it is back.
   */
  setPower(settings: PowerSettings): void {
    this.opts.power?.setDesired(this.computer.id, settings);
    this.set({ powerError: null });
    if (this.client.ready) {
      this.powerAsked = true;
      this.client.setPower(settings);
    }
    this.set({}); // the pending choice is part of what the screen shows
  }

  /** The pending choice, if the computer has not confirmed it yet. */
  pendingPower(): PowerSettings | null {
    const d = this.opts.power?.get(this.computer.id).desired ?? null;
    const s = this.snap.power?.settings;
    return d && !(s && samePowerSettings(d, s)) ? d : null;
  }

  private onPowerState(state: PowerState): void {
    this.powerAsked = false;
    const prefs = this.opts.power;
    if (prefs) {
      const desired = prefs.get(this.computer.id).desired;
      if (desired && samePowerSettings(desired, state.settings)) prefs.setDesired(this.computer.id, null);
      else if (desired && this.client.ready && this.snap.power === null) this.client.setPower(desired); // confirmed state arrived first: now push the pending choice
      const { mac, broadcast, enabled } = state.wake;
      prefs.setWake(this.computer.id, mac && broadcast ? { mac, broadcast, enabled } : null);
    }
    this.set({ power: state, powerUnsupported: false, powerError: null });
  }

  /** Where this phone could send a wake packet for this computer, if it has seen it online with wake information. */
  wakeTarget() {
    return this.opts.power?.get(this.computer.id).wake ?? null;
  }

  /**
   * Ask this (online) computer to send a wake packet to another computer on its network. Resolves with whether the packet was
   * sent: not whether anything woke up. Only one at a time.
   */
  sendWake(mac: string, broadcast: string): Promise<{ ok: boolean; message?: string }> {
    if (!this.client.ready) return Promise.resolve({ ok: false, message: "Not connected to this computer" });
    if (this.wakeCall) clearTimeout(this.wakeCall.timer);
    return new Promise((resolve) => {
      const timer = setTimeout(() => {
        this.wakeCall = null;
        resolve({ ok: false, message: "That computer did not answer" });
      }, this.requestTimeoutMs);
      this.wakeCall = { resolve, timer };
      this.client.sendWake(mac, broadcast);
    });
  }

  /** Reconnect immediately (app returned to the foreground, network came back). */
  reconnectNow(): void {
    this.client.reconnectNow();
  }

  // ---- connection state ------------------------------------------------------------

  private onClientState(state: "connecting" | "ready" | "disconnected" | "failed" | "paused", detail?: string): void {
    switch (state) {
      case "connecting":
        // While the computer is known to be offline, retries should not flicker the banner.
        if (this.snap.state !== "offline") this.set({ state: this.everReady ? "reconnecting" : "connecting" });
        if (this.snap.paused) this.set({ paused: false });
        break;
      case "paused":
        this.clearPending(new Error("Connection lost"));
        this.settleCheck("offline");
        this.set({ state: "offline", paused: true });
        break;
      case "ready":
        this.everReady = true;
        this.reportedPlan = null;
        this.readyAt = this.now();
        this.set({ state: "ready", paused: false, blocked: null });
        this.settleCheck("online");
        // A switch's answer that went out on the old link is lost: stop waiting for it, and take the session list's word on
        // which way each session runs (a switch still going on the computer is announced to this link when it finishes).
        for (const store of this.chats.values()) if (store.getSnapshot().switching) store.patch({ switching: false });
        this.refreshSessions();
        this.client.listInbox();
        this.client.getAccount();
        this.askPower();
        if (this.accountToken) {
          this.client.attachAccount(this.accountToken); // a signed-in phone signs its computer in too
          this.releaseAttach(false);
          this.attaching = setTimeout(() => this.releaseAttach(true), ATTACH_GRACE_MS);
        }
        if (this.pushEndpoint !== undefined) this.client.setPushEndpoint(this.pushEndpoint, this.pushExtra); // the computer forgets nothing, but a new address must reach it
        break;
      case "disconnected": {
        if (detail === "stopped") break;
        const code = Number(detail?.split(" ")[0]);
        // 4404: the relay has no such daemon. 4503: the daemon just went away.
        const offline = code === 4404 || code === 4503;
        this.clearPending(new Error("Connection lost"));
        if (offline) this.settleCheck("offline");
        this.set({
          state: offline ? "offline" : this.everReady ? "reconnecting" : "connecting",
          devicesStatus: this.snap.devicesStatus === "loading" ? "error" : this.snap.devicesStatus,
        });
        break;
      }
      case "failed":
        if (detail === "guest_computer_limit") {
          // Not a removal: a guest phone may use one computer. The app asks the person to sign in, which restarts this link.
          this.clearPending(new Error("Sign in to use another computer"));
          this.set({ state: "offline", paused: true, blocked: "guest_computer" });
          break;
        }
        this.clearPending(new Error("This phone was removed from this computer"));
        this.set({ state: "unauthorized" });
        break;
    }
  }

  // ---- sessions --------------------------------------------------------------------

  /** The list is pulled, not pushed: call this on open, after changes, and on refresh. */
  refreshSessions(): void {
    if (!this.client.ready) return;
    this.set({ sessionsStatus: this.snap.sessionsUpdatedAt === null ? "loading" : "refreshing" });
    this.client.listSessions();
  }

  createSession(opts: { command: string; args?: string[]; cwd?: string; cols?: number; rows?: number; title?: string; takeOver?: string; force?: boolean }): Promise<SessionInfo> {
    if (!this.client.ready) return Promise.reject(new Error("Not connected to this computer"));
    if (this.creating) return Promise.reject(new Error("Already starting a session"));
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        this.creating = null;
        reject(new Error("The computer did not answer in time"));
      }, this.requestTimeoutMs);
      this.creating = { resolve, reject, timer };
      this.client.createSession(opts);
    });
  }

  private pushEndpoint: string | null | undefined;
  private pushExtra: PushExtra | undefined;

  private accountToken: string | null = null;
  /** This phone's Brocoded sign-in (or null when signed out): sent to the computer whenever we connect, so it links itself to the account. */
  setAccountToken(token: string | null): void {
    this.accountToken = token;
    if (token && this.client.ready) this.client.attachAccount(token);
    if (token) this.retryBlocked(); // the relay turned this phone away as a guest; try again as the signed-in account
  }

  /** Try the link again after the relay turned this phone away as a guest and something changed (signed in, or chose this computer). */
  retryBlocked(): void {
    if (!this.snap.blocked) return;
    this.set({ blocked: null, paused: false, state: "connecting" });
    this.client.start();
  }

  /** Sign this computer out of the phone's account (the phone is signing out). Resolves once it answers, or after a few seconds. */
  detachAccount(token: string): Promise<void> {
    return new Promise((resolve) => {
      if (!this.client.ready) return resolve();
      const done = () => {
        off();
        clearTimeout(timer);
        resolve();
      };
      const off = this.subscribe(() => {
        if (this.snap.account && !this.snap.account.signedIn) done();
      });
      const timer = setTimeout(done, 3000);
      this.client.detachAccount(token);
    });
  }

  /**
   * Where the computer should wake this phone when something needs it (null: stop). Remembered, and re-sent every
   * time the connection comes back, so a changed address reaches the computer.
   */
  setPushEndpoint(endpoint: string | null, extra?: PushExtra): void {
    this.pushEndpoint = endpoint;
    this.pushExtra = extra;
    if (this.client.ready) this.client.setPushEndpoint(endpoint, extra);
  }

  /** Stop a running session's program. Its terminal shows "ended" once the computer confirms. */
  endSession(id: string): boolean {
    return this.client.endSession(id);
  }

  /** Forget an ended session on the computer, and drop it from the list. */
  removeSession(id: string): boolean {
    return this.client.removeSession(id);
  }

  /** Forget every ended session. */
  clearEndedSessions(): number {
    const ended = this.snap.sessions.filter((s) => s.status !== "running");
    for (const s of ended) this.client.removeSession(s.id);
    return ended.length;
  }

  /** Folders inside `path` on the computer, or its home folder and recent projects when omitted. */
  listFolders(path?: string): Promise<FolderListing> {
    if (!this.client.ready) return Promise.reject(new Error("Not connected to this computer"));
    return new Promise((resolve, reject) => {
      this.folderRequests.push({ resolve, reject, path, sent: false, timer: undefined as unknown as ReturnType<typeof setTimeout> });
      this.pumpFolders();
    });
  }

  /**
   * Ask the computer whether voice input is ready (the answer lands in `snapshot.voice`). Asked when the person first reaches for the
   * microphone, not at connect time, so an older computer's "unsupported" can't be mistaken for an answer to anything else.
   */
  private homeTimer: ReturnType<typeof setTimeout> | null = null;

  /** Save what Home shows, a moment after the last change (a burst of updates is one write). */
  private persistHome(): void {
    if (!this.opts.homeCache || this.homeTimer) return;
    this.homeTimer = setTimeout(() => this.flushHome(), 800);
  }

  /** Write now what a pending save would write (also when the connection stops, so the last state is not lost). */
  private flushHome(): void {
    if (this.homeTimer) clearTimeout(this.homeTimer);
    const wasPending = this.homeTimer !== null;
    this.homeTimer = null;
    const cache = this.opts.homeCache;
    if (!cache || !wasPending) return;
    try {
      cache.save(this.computer.id, { sessions: this.snap.sessions, office: this.snap.office ?? null, savedAt: this.snap.sessionsUpdatedAt ?? this.now() });
    } catch {
      /* a full or blocked store just means nothing is remembered */
    }
  }

  // ---- the team (see packages/app-core/src/office.ts) -----------------------------------------------------------

  private onOffice(reply: OfficeReply): void {
    switch (reply.t) {
      case "office.state": {
        if (this.officeAsked) clearTimeout(this.officeAsked);
        this.officeAsked = null;
        const { t: _t, ...office } = reply;
        this.set({ office, officeUnsupported: false });
        this.persistHome();
        return;
      }
      case "office.activity":
        this.set({ officeActivity: addActivity(this.snap.officeActivity ?? {}, reply.entry) });
        return;
      case "office.message": {
        const held = this.snap.officeMessages ?? [];
        const next = addMessage(held, reply.message);
        if (next !== held) this.set({ officeMessages: next, officeLive: reply.message });
        return;
      }
      default: {
        const p = this.officeCalls.get(reply.rid);
        if (!p) return;
        this.officeCalls.delete(reply.rid);
        clearTimeout(p.timer);
        p.resolve(reply);
      }
    }
  }

  /** Ask the computer for its team. The answer arrives in the snapshot (`office`); a computer too old to know teams never answers, so silence marks it unsupported. */
  refreshOffice(): void {
    if (!this.client.ready) return;
    if (!this.officeAsked) {
      this.officeAsked = setTimeout(() => {
        this.officeAsked = null;
        if (!this.snap.office) this.set({ officeUnsupported: true });
      }, 8_000);
    }
    this.client.getOffice();
  }

  /** One request, one reply by `rid`. Never rejects: a lost link or a silent (older) computer comes back as `undefined`. */
  private officeCall<T extends OfficeReply["t"]>(want: T, send: (rid: string) => boolean, timeoutMs = 10_000): Promise<Extract<OfficeReply, { t: T }> | undefined> {
    if (!this.client.ready) return Promise.resolve(undefined);
    const rid = `o${++this.officeSeq}`;
    return new Promise((resolve) => {
      const timer = setTimeout(() => {
        this.officeCalls.delete(rid);
        resolve(undefined);
      }, timeoutMs);
      this.officeCalls.set(rid, { resolve: (r) => resolve(r.t === want ? (r as Extract<OfficeReply, { t: T }>) : undefined), timer });
      if (!send(rid)) {
        clearTimeout(timer);
        this.officeCalls.delete(rid);
        resolve(undefined);
      }
    });
  }

  private static readonly NOT_REACHED = "Couldn't reach this computer.";

  /** One page of what agents did, newest first. `before` is the previous page's `nextBefore`. Entries are also folded into the live buffer. */
  async officeHistory(q: { agentId?: string; before?: string; limit?: number } = {}): Promise<{ entries: OfficeActivity[]; nextBefore: string | null; ok: boolean }> {
    const r = await this.officeCall("office.history.listed", (rid) => this.client.officeHistory(rid, q));
    if (!r) return { entries: [], nextBefore: null, ok: false };
    // The newest page seeds the live buffer, so a screen opened after the fact isn't empty until something happens.
    if (!q.before && r.entries.length > 0) {
      let buf = this.snap.officeActivity ?? {};
      for (const e of r.entries) buf = addActivity(buf, e);
      if (buf !== this.snap.officeActivity) this.set({ officeActivity: buf });
    }
    return { entries: r.entries, nextBefore: r.nextBefore, ok: true };
  }

  /** Load the latest messages the team passed on (call when the team screen opens, so the chat is not empty until something happens). */
  async officeLoadMessages(limit = 60): Promise<boolean> {
    const r = await this.officeCall("office.messages.listed", (rid) => this.client.officeMessages(rid, limit));
    if (!r) return false;
    this.set({ officeMessages: mergeMessages(this.snap.officeMessages ?? [], r.messages) });
    return true;
  }

  async officeSummary(agentId: string): Promise<OfficeSummary | null> {
    const r = await this.officeCall("office.summary.shown", (rid) => this.client.officeSummary(rid, agentId));
    return r?.summary ?? null;
  }

  async officeTerminal(agentId: string): Promise<{ ok: boolean; text: string; idleMs: number | null; message?: string }> {
    const r = await this.officeCall("office.terminal.shown", (rid) => this.client.officeTerminal(rid, agentId));
    if (!r) return { ok: false, text: "", idleMs: null, message: ComputerConnection.NOT_REACHED };
    return { ok: r.ok, text: r.text ?? "", idleMs: r.idleMs ?? null, ...(r.message ? { message: r.message } : {}) };
  }

  private async officeAct(send: (rid: string) => boolean): Promise<{ ok: boolean; message?: string }> {
    const r = await this.officeCall("office.done", send);
    if (!r) return { ok: false, message: ComputerConnection.NOT_REACHED };
    return { ok: r.ok, ...(r.message ? { message: r.message } : {}) };
  }

  /** Give the team a goal; its orchestrator splits it up. */
  officeSend(body: string): Promise<{ ok: boolean; message?: string }> {
    return this.officeAct((rid) => this.client.officeSend(rid, body));
  }

  officeControl(agentId: string, action: "pause" | "resume" | "halt" | "steer", text?: string): Promise<{ ok: boolean; message?: string }> {
    return this.officeAct((rid) => this.client.officeControl(rid, agentId, action, text));
  }

  officeInput(agentId: string, text: string, enter = false): Promise<{ ok: boolean; message?: string }> {
    return this.officeAct((rid) => this.client.officeInput(rid, agentId, text, enter));
  }

  /** Start or stop the team on this computer. */
  officeEngine(action: "start" | "stop"): Promise<{ ok: boolean; message?: string }> {
    return this.officeAct((rid) => this.client.officeEngine(rid, action));
  }

  /** Show or hide the team engine's own window on the computer. */
  officeWindow(visible: boolean): Promise<{ ok: boolean; message?: string }> {
    return this.officeAct((rid) => this.client.officeWindow(rid, visible));
  }

  /** Ask the computer to read the agents' plan limits (the answer arrives in the snapshot). `force` asks for a fresh reading; the computer still reuses one under a minute old. */
  refreshUsage(force = false): void {
    if (!this.client.ready || this.usageAsked) return;
    this.usageAsked = true;
    this.set({ usageLoading: true });
    if (!this.client.getUsage(force)) {
      this.usageAsked = false;
      this.set({ usageLoading: false });
    }
  }

  /** Ask the computer for its approval rules (the answer arrives in the snapshot). */
  refreshRules(): void {
    if (!this.client.ready || this.rulesAsked) return;
    this.rulesAsked = true;
    if (!this.client.getRules()) this.rulesAsked = false;
  }

  /** Save a rule on the computer. `onRefused` hears why when the computer says no. The new list arrives in the snapshot. */
  addRule(rule: Omit<ApprovalRule, "id" | "createdAt">, onRefused?: (message: string) => void): boolean {
    this.ruleError = onRefused ?? null;
    return this.client.addRule(rule);
  }

  removeRule(id: string): boolean {
    return this.client.removeRule(id);
  }

  refreshVoice(): void {
    if (!this.client.ready || this.voiceAsked) return;
    this.voiceAsked = true;
    if (!this.client.getVoice()) this.voiceAsked = false;
  }

  /**
   * Turn a short recording into text on the computer (the audio travels inside the encrypted link and is not kept anywhere).
   * Rejects with a sentence that can be shown to the person.
   */
  transcribe(audio: Uint8Array, mime: string): Promise<string> {
    return this.sendRecording([audio], mime);
  }

  /**
   * Send 16 kHz mono 16-bit samples (what the phone makes from a recording), in pieces small enough for the relay, and turn them into
   * text on the computer. The computer needs no audio converter for this.
   */
  transcribePcm(samples: Int16Array): Promise<string> {
    const bytes = new Uint8Array(samples.buffer, samples.byteOffset, samples.byteLength);
    const PIECE = 420_000; // base64 adds a third: stays well under the relay's message size
    const pieces: Uint8Array[] = [];
    for (let at = 0; at < bytes.length; at += PIECE) pieces.push(bytes.subarray(at, Math.min(bytes.length, at + PIECE)));
    return this.sendRecording(pieces.length ? pieces : [new Uint8Array(0)], "audio/pcm;rate=16000");
  }

  /** Ask the computer to get voice ready by itself (it downloads the speech model). Progress arrives in the snapshot's `voice.setup`. */
  setupVoice(): boolean {
    if (!this.client.ready) return false;
    this.set({ voice: { ...(this.snap.voice ?? { ready: false }), settingUp: true, setup: { stage: "downloading", percent: 0 } } });
    return this.client.setupVoice();
  }

  /**
   * Send a picture (already shrunk by the phone) to be saved on the computer, in pieces small enough for the relay. Resolves with the
   * path it now has there, which goes into a message so the agent can open it. Rejects with a sentence that can be shown.
   */
  uploadImage(image: Uint8Array, mime: string): Promise<string> {
    if (!this.client.ready) return Promise.reject(new Error("Not connected to this computer"));
    const PIECE = 420_000; // base64 adds a third: stays well under the relay's message size
    const pieces: Uint8Array[] = [];
    for (let at = 0; at < image.length; at += PIECE) pieces.push(image.subarray(at, Math.min(image.length, at + PIECE)));
    if (pieces.length > 16) return Promise.reject(new Error("That picture is too large."));
    const rid = `i${++this.imageSeq}`;
    return new Promise<string>((resolve, reject) => {
      const timer = setTimeout(() => {
        this.imageCalls.delete(rid);
        reject(new Error("Your computer took too long to answer. Try again."));
      }, Math.max(this.requestTimeoutMs, 60_000));
      this.imageCalls.set(rid, { resolve, reject, timer });
      const fail = () => {
        clearTimeout(timer);
        this.imageCalls.delete(rid);
        reject(new Error("Not connected to this computer"));
      };
      if (pieces.length <= 1) {
        if (!this.client.uploadImage(rid, mime, toBase64(pieces[0] ?? new Uint8Array(0)))) fail();
        return;
      }
      for (let i = 0; i < pieces.length; i++) {
        if (!this.client.uploadImage(rid, mime, toBase64(pieces[i]!), { seq: i, last: i === pieces.length - 1 })) return fail();
      }
    });
  }

  private sendRecording(pieces: Uint8Array[], mime: string): Promise<string> {
    if (!this.client.ready) return Promise.reject(new Error("Not connected to this computer"));
    const rid = `v${++this.voiceSeq}`;
    return new Promise<string>((resolve, reject) => {
      const timer = setTimeout(() => {
        this.voiceCalls.delete(rid);
        reject(new Error("Your computer took too long to answer. Try a shorter recording."));
      }, Math.max(this.requestTimeoutMs, 120_000));
      this.voiceCalls.set(rid, { resolve, reject, timer });
      const fail = () => {
        clearTimeout(timer);
        this.voiceCalls.delete(rid);
        reject(new Error("Not connected to this computer"));
      };
      if (pieces.length === 1) {
        if (!this.client.transcribe(rid, mime, toBase64(pieces[0]!))) fail();
        return;
      }
      for (let i = 0; i < pieces.length; i++) {
        if (!this.client.transcribe(rid, mime, toBase64(pieces[i]!), { seq: i, last: i === pieces.length - 1 })) return fail();
      }
    });
  }

  /**
   * The skill library on the computer: list it, add skills from a git address, link a skill to an agent, and so on. Never rejects:
   * a computer that is too old to know skills (it never answers) comes back as `ok: false`.
   */
  skills(op: SkillOp, cwd?: string): Promise<SkillsReply> {
    const fail = (message: string): SkillsReply => ({ ok: false, message, skills: [], library: "" });
    if (!this.client.ready) return Promise.resolve(fail("Not connected to this computer"));
    const rid = `s${++this.skillSeq}`;
    return new Promise((resolve) => {
      const timer = setTimeout(() => {
        this.skillCalls.delete(rid);
        resolve(fail(op.op === "add" || op.op === "install" ? "The computer took too long. Try again." : "This computer's Brocoded is too old for skills. Update it."));
      }, op.op === "add" || op.op === "install" ? 150_000 : 20_000);
      this.skillCalls.set(rid, { resolve, timer });
      if (!this.client.skillsDo(rid, op, cwd)) {
        clearTimeout(timer);
        this.skillCalls.delete(rid);
        resolve(fail("Not connected to this computer"));
      }
    });
  }

  /** Files and folders in a session's folder that match `query` (for `@file` mentions). Resolves empty if the computer is too old to answer. */
  findFiles(sessionId: string, query: string): Promise<{ results: string[]; truncated: boolean }> {
    const none = { results: [], truncated: false };
    if (!this.client.ready) return Promise.resolve(none);
    const rid = `a${++this.fileSeq}`;
    return new Promise((resolve) => {
      const timer = setTimeout(() => {
        this.fileCalls.delete(rid);
        resolve(none);
      }, 6_000);
      this.fileCalls.set(rid, { resolve, timer });
      if (!this.client.findFiles(rid, sessionId, query)) {
        clearTimeout(timer);
        this.fileCalls.delete(rid);
        resolve(none);
      }
    });
  }

  /**
   * Folders whose name matches `query` anywhere under `root` (the home folder when omitted). Rejects if the computer is too old
   * to search (it never answers) or can't search there, so the picker can say so instead of waiting.
   */
  findFolders(query: string, root?: string): Promise<FolderSearch> {
    if (!this.client.ready) return Promise.reject(new Error("Not connected to this computer"));
    const rid = `f${++this.findSeq}`;
    return new Promise<FolderSearch>((resolve, reject) => {
      const timer = setTimeout(() => {
        this.findCalls.delete(rid);
        reject(new Error("The computer did not answer in time"));
      }, Math.max(this.requestTimeoutMs, 12_000));
      this.findCalls.set(rid, { resolve, reject, timer });
      if (!this.client.findFolders(rid, query, root)) {
        clearTimeout(timer);
        this.findCalls.delete(rid);
        reject(new Error("Not connected to this computer"));
      }
    });
  }

  /**
   * Earlier agent conversations in a folder (newest first), each with the command that resumes it. Rejects if this
   * computer is too old to know the request, so callers can simply show nothing.
   */
  listHistory(cwd: string): Promise<HistoryEntry[]> {
    if (!this.client.ready) return Promise.reject(new Error("Not connected to this computer"));
    const existing = this.historyRequests.get(cwd);
    if (existing) return existing.promise;
    let resolve!: (items: HistoryEntry[]) => void;
    let reject!: (e: Error) => void;
    const promise = new Promise<HistoryEntry[]>((res, rej) => ((resolve = res), (reject = rej)));
    const timer = setTimeout(() => {
      this.historyRequests.delete(cwd);
      reject(new Error("The computer did not answer in time"));
    }, this.requestTimeoutMs);
    this.historyRequests.set(cwd, { promise, resolve, reject, timer });
    this.client.listHistory(cwd);
    return promise;
  }

  /** Earlier messages of a session's conversation (the latest page, or the page before cursor `before`). */
  getTranscript(sessionId: string, before?: number): Promise<TranscriptReply> {
    return this.transcriptCall(sessionId, () => this.client.listTranscript(sessionId, before));
  }

  /** Only what a conversation gained after `after` (the `end` of the previous reply): following a chat live. */
  followTranscript(sessionId: string, after: number): Promise<TranscriptReply> {
    return this.transcriptCall(sessionId, () => this.client.followTranscript(sessionId, after));
  }

  private transcriptCall(sessionId: string, send: () => void): Promise<TranscriptReply> {
    if (!this.client.ready) return Promise.reject(new Error("Not connected to this computer"));
    if (this.transcriptRequests.has(sessionId)) return Promise.reject(new Error("Already loading earlier messages"));
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        this.transcriptRequests.delete(sessionId);
        reject(new Error("The computer did not answer in time"));
      }, this.requestTimeoutMs);
      this.transcriptRequests.set(sessionId, { resolve, reject, timer });
      send();
    });
  }

  /**
   * What has changed (git) in the folder a session runs in; with `against`, compared with where this branch left that one.
   * Rejects if the computer is too old to know the request.
   */
  getChanges(sessionId: string, against?: string): Promise<GitDiffReply> {
    if (!this.client.ready) return Promise.reject(new Error("Not connected to this computer"));
    // a name the computer would refuse to even read as a message must not look like "update Brocoded"
    if (against !== undefined && !GIT_REF_RE.test(against)) return Promise.reject(new Error("That branch name has characters Brocoded can't use from the phone."));
    const key = diffKey(sessionId, against);
    const existing = this.diffRequests.get(key);
    if (existing) return existing.promise;
    let resolve!: (r: GitDiffReply) => void;
    let reject!: (e: Error) => void;
    const promise = new Promise<GitDiffReply>((res, rej) => ((resolve = res), (reject = rej)));
    const timer = setTimeout(() => {
      this.diffRequests.delete(key);
      reject(new Error("The computer did not answer in time"));
    }, Math.max(this.requestTimeoutMs, 20_000)); // git on a big repository can take a moment
    this.diffRequests.set(key, { promise, resolve, reject, timer });
    this.client.gitDiff(sessionId, against);
    return promise;
  }

  private gitCall<R extends GitReply>(sessionId: string, send: (rid: string) => boolean, timeoutMs: number): Promise<R> {
    if (!this.client.ready) return Promise.reject(new Error("Not connected to this computer"));
    const rid = `g${++this.gitSeq}`;
    return new Promise<R>((res, rej) => {
      const timer = setTimeout(() => {
        this.gitCalls.delete(rid);
        rej(new Error("The computer did not answer in time"));
      }, timeoutMs);
      this.gitCalls.set(rid, { sessionId, resolve: res as (r: GitReply) => void, reject: rej, timer });
      if (!send(rid)) {
        clearTimeout(timer);
        this.gitCalls.delete(rid);
        rej(new Error("Not connected to this computer"));
      }
    });
  }

  private readonly GIT_WAIT = () => Math.max(this.requestTimeoutMs, 30_000);

  getBranches(sessionId: string) {
    return this.gitCall<Extract<GitReply, { t: "git.branched" }>>(sessionId, (rid) => this.client.gitBranches(sessionId, rid), this.GIT_WAIT());
  }

  getLog(sessionId: string, skip = 0, limit = 30) {
    return this.gitCall<Extract<GitReply, { t: "git.logged" }>>(sessionId, (rid) => this.client.gitLog(sessionId, rid, skip, limit), this.GIT_WAIT());
  }

  getCommit(sessionId: string, hash: string) {
    return this.gitCall<Extract<GitReply, { t: "git.shown" }>>(sessionId, (rid) => this.client.gitShow(sessionId, rid, hash), this.GIT_WAIT());
  }

  /** Stage, commit, switch, push... A refusal comes back as `{ ok: false, code, message, details }`, not as a rejection. */
  gitDo(sessionId: string, op: GitOp, ignoreBusy?: boolean) {
    const bad = GitOpSchema.safeParse(op);
    if (!bad.success) {
      const branchy = op.kind === "switch" || op.kind === "create";
      return Promise.resolve<Extract<GitReply, { t: "git.done" }>>({
        t: "git.done",
        sessionId,
        rid: "local",
        ok: false,
        code: branchy ? "bad_branch" : "bad_request",
        message: branchy ? "That isn't a valid branch name. Use letters, numbers, - _ . and /." : op.kind === "commit" ? "That commit message is too long." : "That request isn't valid.",
      });
    }
    // a push may take up to a minute on the computer, so wait a little longer than that
    return this.gitCall<Extract<GitReply, { t: "git.done" }>>(sessionId, (rid) => this.client.gitDo(sessionId, rid, op, ignoreBusy), Math.max(this.requestTimeoutMs, 75_000));
  }

  /** Send the next folder request if none is waiting for its answer. */
  private pumpFolders(): void {
    const next = this.folderRequests[0];
    if (!next || next.sent) return;
    next.sent = true;
    // generous: the first open of Desktop/Documents waits for the person at the Mac to click Allow
    next.timer = setTimeout(() => {
      const i = this.folderRequests.indexOf(next);
      if (i >= 0) this.folderRequests.splice(i, 1);
      next.reject(new Error("The computer did not answer in time"));
      this.pumpFolders();
    }, Math.max(this.requestTimeoutMs, 90_000));
    if (!this.client.listFolders(next.path)) {
      clearTimeout(next.timer);
      this.folderRequests.shift();
      next.reject(new Error("Not connected to this computer"));
      this.pumpFolders();
    }
  }

  private onCreated(session: SessionInfo): void {
    this.set({ sessions: sort([session, ...this.snap.sessions.filter((s) => s.id !== session.id)]) });
    if (this.creating) {
      clearTimeout(this.creating.timer);
      this.creating.resolve(session);
      this.creating = null;
    }
  }

  /**
   * Attach a terminal view to a session: the whole retained output is replayed to `listener`, then
   * live output follows, across reconnects. Returns a function that detaches. One view per session.
   */
  openSession(id: string, listener: SessionListener): () => void {
    this.terminals.set(id, listener);
    this.client.attach(id, 0);
    return () => {
      if (this.terminals.get(id) === listener) {
        this.terminals.delete(id);
        this.client.detach(id);
      }
    };
  }

  exitCodeOf(id: string): number | null | undefined {
    return this.exits.get(id);
  }

  input(id: string, data: string | Uint8Array): boolean {
    return this.client.input(id, data);
  }

  // ---- chat ------------------------------------------------------------------------

  private readonly chats = new Map<string, ChatStore>();

  /** A session's chat (and which way it runs), as an observable store. */
  chat(sessionId: string): ChatStore {
    let store = this.chats.get(sessionId);
    if (!store) {
      store = new ChatStore(this.snap.sessions.find((s) => s.id === sessionId)?.mode ?? "terminal");
      this.chats.set(sessionId, store);
    }
    return store;
  }

  /** Run the session as a chat, or back in its terminal. The answer arrives as the store's `mode` / `reason`. */
  setMode(sessionId: string, mode: "terminal" | "chat"): boolean {
    const store = this.chat(sessionId);
    if (!this.client.setMode(sessionId, mode)) {
      store.patch({ reason: "Not connected to this computer." });
      return false;
    }
    store.patch({ switching: true, reason: null });
    return true;
  }

  /** Follow a session's chat while a screen shows it. Returns the stop function. */
  followChat(sessionId: string): () => void {
    this.client.chatAttach(sessionId);
    return () => this.client.chatDetach(sessionId);
  }

  chatSend(sessionId: string, text: string): boolean {
    const ok = this.client.chatSend(sessionId, text);
    if (!ok) this.chat(sessionId).patch({ error: "Not connected to this computer." });
    return ok;
  }

  chatInterrupt(sessionId: string): boolean {
    return this.client.chatInterrupt(sessionId);
  }

  /** Allow everything the agent asks in this chat (or stop doing so). The change comes back to every phone as the chat's `allowAll`. */
  chatAllowAll(sessionId: string, on: boolean): boolean {
    return this.client.chatAllowAll(sessionId, on);
  }

  chatApprove(sessionId: string, reqId: string, allow: boolean): boolean {
    return this.client.chatApprove(sessionId, reqId, allow);
  }

  private onChat(m: ChatMessage): void {
    if (m.t === "session.mode") {
      const known = this.snap.sessions.find((s) => s.id === m.sessionId);
      if (known && (known.mode ?? "terminal") !== m.mode) this.set({ sessions: this.snap.sessions.map((s) => (s.id === m.sessionId ? { ...s, mode: m.mode } : s)) });
    }
    this.chat(m.sessionId).apply(m);
  }

  resize(id: string, cols: number, rows: number): boolean {
    return this.client.resize(id, cols, rows);
  }

  private onExit(id: string, code: number | null): void {
    this.exits.set(id, code);
    this.set({ sessions: this.snap.sessions.map((s) => (s.id === id ? { ...s, status: "exited" as const } : s)) });
    this.terminals.get(id)?.onExit(code);
  }

  // ---- inbox -----------------------------------------------------------------------

  private onInboxEvent(event: InboxEvent): void {
    this.set({ inbox: [event, ...this.snap.inbox.filter((e) => e.id !== event.id)].sort(byNewest) });
    const waiting = this.responding.get(event.id);
    if (waiting && event.state === "resolved") {
      clearTimeout(waiting.timer);
      this.responding.delete(event.id);
      waiting.resolve();
    }
  }

  /**
   * Approve or deny an approval, or answer a question. Resolves once the computer confirms the event is
   * resolved; rejects if the computer says it can't (already handled, unknown) or doesn't answer in time.
   */
  respondInbox(eventId: string, action: InboxAction, answers?: string[]): Promise<void> {
    if (!this.client.ready) return Promise.reject(new Error("Not connected to this computer"));
    if (this.responding.has(eventId)) return Promise.reject(new Error("Already sending a response"));
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        this.responding.delete(eventId);
        reject(new Error("The computer did not answer in time"));
      }, this.requestTimeoutMs);
      this.responding.set(eventId, { resolve, reject, timer });
      this.client.respondInbox(eventId, action, answers);
    });
  }

  /**
   * Remove a card. Refused for an approval or question that is still open (answer it first), and only takes effect
   * locally if the computer was reachable.
   */
  dismissInbox(eventId: string): boolean {
    const event = this.snap.inbox.find((e) => e.id === eventId);
    if (event && event.state === "open" && (event.kind === "approval" || event.kind === "question")) return false;
    if (!this.client.dismissInbox(eventId)) return false;
    this.set({ inbox: this.snap.inbox.filter((e) => e.id !== eventId) });
    return true;
  }

  // ---- devices ---------------------------------------------------------------------

  loadDevices(): void {
    if (!this.client.ready) return void this.set({ devicesStatus: "error" });
    this.set({ devicesStatus: "loading" });
    if (this.devicesTimer) clearTimeout(this.devicesTimer);
    this.devicesTimer = setTimeout(() => {
      if (this.snap.devicesStatus === "loading") this.set({ devicesStatus: "error" });
    }, this.requestTimeoutMs);
    this.client.listDevices();
  }

  private onDevices(devices: DeviceInfo[]): void {
    if (this.devicesTimer) clearTimeout(this.devicesTimer);
    this.set({ devices, devicesStatus: "ready" });
  }

  /** Remove a device from the computer. Removing this phone ends its access (state becomes "unauthorized"). */
  removeDevice(deviceId: string): Promise<void> {
    if (!this.client.ready) return Promise.reject(new Error("Not connected to this computer"));
    if (this.revoking) return Promise.reject(new Error("Already removing a device"));
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        this.revoking = null;
        reject(new Error("The computer did not answer in time"));
      }, this.requestTimeoutMs);
      this.revoking = { resolve, reject, timer };
      this.client.revokeDevice(deviceId);
    });
  }

  private onDeviceRevoked(deviceId: string): void {
    this.set({ devices: this.snap.devices.filter((d) => d.id !== deviceId) });
    if (this.revoking) {
      clearTimeout(this.revoking.timer);
      this.revoking.resolve();
      this.revoking = null;
    }
  }

  // ---- errors ----------------------------------------------------------------------

  private readonly noticeListeners = new Set<(message: string) => void>();
  /** Things the computer told us that no screen is waiting on (e.g. "update Brocoded on your computer"). */
  onNotice(cb: (message: string) => void): () => void {
    this.noticeListeners.add(cb);
    return () => this.noticeListeners.delete(cb);
  }

  private onError(e: { sessionId?: string; eventId?: string; code: string; message: string; feature?: string }): void {
    // The app asks for the power state the moment a link is ready, before anything else, and replies come back in order: so the first
    // "unsupported" after that ask is an older computer's answer to it, not to a git request that was sent a moment later.
    if (e.code === "unsupported" && this.powerAsked) {
      this.powerAsked = false;
      this.opts.power?.setDesired(this.computer.id, null);
      return this.set({ powerUnsupported: true });
    }
    if (e.code === "usage_failed" && this.usageAsked) {
      this.usageAsked = false;
      return this.set({ usageLoading: false });
    }
    if (e.code === "unsupported" && this.usageAsked) {
      this.usageAsked = false;
      return this.set({ usage: null, usageUnsupported: true, usageLoading: false });
    }
    if (e.code === "rule_refused") {
      const tell = this.ruleError;
      this.ruleError = null;
      if (tell) return tell(e.message);
    }
    if (e.code === "unsupported" && this.rulesAsked) {
      this.rulesAsked = false;
      return this.set({ rules: null, rulesUnsupported: true });
    }
    if (e.code === "unsupported" && this.imageCalls.size > 0) {
      for (const [rid, c] of [...this.imageCalls]) {
        this.imageCalls.delete(rid);
        clearTimeout(c.timer);
        c.reject(new Error("Update Brocoded on your computer to send pictures."));
      }
      return;
    }
    if (e.code === "unsupported" && (this.voiceAsked || this.voiceCalls.size > 0)) {
      const update = "Update Brocoded on your computer to use voice.";
      this.voiceAsked = false;
      for (const [rid, c] of [...this.voiceCalls]) {
        this.voiceCalls.delete(rid);
        clearTimeout(c.timer);
        c.reject(new Error(update));
      }
      return this.set({ voice: { ready: false, unsupported: true, reason: update } });
    }
    if (e.code === "unsupported" && (this.diffRequests.size > 0 || this.gitCalls.size > 0)) {
      for (const [id, d] of [...this.diffRequests]) {
        this.diffRequests.delete(id);
        clearTimeout(d.timer);
        d.reject(new Error("Update Brocoded on your computer to see changes."));
      }
      for (const [id, c] of [...this.gitCalls]) {
        this.gitCalls.delete(id);
        clearTimeout(c.timer);
        c.reject(new Error("Update Brocoded on your computer"));
      }
      return;
    }
    if (e.code === "unsupported") {
      // an older computer didn't understand a request: whoever is waiting on an answer learns why; otherwise tell the user
      const waiting = this.folderRequests[0]?.sent ? this.folderRequests.shift() : undefined;
      if (waiting) {
        clearTimeout(waiting.timer);
        waiting.reject(errOf(e));
        this.pumpFolders();
      } else if (this.historyRequests.size > 0) {
        for (const [cwd, h] of [...this.historyRequests]) {
          this.historyRequests.delete(cwd);
          clearTimeout(h.timer);
          h.reject(errOf(e)); // history is a nicety: the caller just shows none
        }
      } else if (this.now() - this.readyAt < BACKGROUND_REQUESTS_MS) {
        // The app asks a computer about its account (and gives it a push address) every time it connects. An older Brocoded
        // answers "unsupported" to those; nobody tapped anything, so saying "update Brocoded" on every connect is just noise.
      } else {
        for (const l of [...this.noticeListeners]) l(e.message);
      }
      return;
    }
    if (e.sessionId && (e.code === "chat_busy" || e.code === "not_chat" || e.code === "approval_closed")) {
      return void this.chats.get(e.sessionId)?.patch({ error: e.message });
    }
    if (e.sessionId && this.transcriptRequests.has(e.sessionId) && (e.code === "no_transcript" || e.code === "no_such_session" || e.code === "upgrade_required")) {
      const p = this.transcriptRequests.get(e.sessionId)!;
      this.transcriptRequests.delete(e.sessionId);
      clearTimeout(p.timer);
      return void p.reject(errOf(e));
    }
    if (e.sessionId) {
      let hit = false;
      for (const [k, d] of [...this.diffRequests]) {
        if (!k.startsWith(e.sessionId + "\0")) continue;
        this.diffRequests.delete(k);
        clearTimeout(d.timer);
        d.reject(errOf(e));
        hit = true;
      }
      for (const [k, c] of [...this.gitCalls]) {
        if (c.sessionId !== e.sessionId) continue;
        this.gitCalls.delete(k);
        clearTimeout(c.timer);
        c.reject(errOf(e));
        hit = true;
      }
      if (hit) return;
    }
    if (e.sessionId) return void this.terminals.get(e.sessionId)?.onError({ code: e.code, message: e.message });
    if (e.code === "power_failed") {
      this.powerAsked = false;
      this.opts.power?.setDesired(this.computer.id, null);
      return this.set({ powerError: e.message });
    }
    if (e.code === "account_failed" || e.code === "account_unavailable") return this.releaseAttach(true); // the computer can't link itself right now: it just stays as it was
    if ((e.code === "spawn_failed" || e.code === "sign_in_required" || e.code === "upgrade_required") && this.creating) {
      clearTimeout(this.creating.timer);
      this.creating.reject(errOf(e));
      this.creating = null;
    } else if ((e.code === "fs_error" || e.code === "fs_denied") && this.folderRequests[0]?.sent) {
      const p = this.folderRequests.shift()!;
      clearTimeout(p.timer);
      p.reject(Object.assign(errOf(e), { denied: e.code === "fs_denied" }));
      this.pumpFolders();
    } else if (e.eventId && this.responding.has(e.eventId)) {
      const p = this.responding.get(e.eventId)!;
      clearTimeout(p.timer);
      this.responding.delete(e.eventId);
      p.reject(errOf(e));
    } else if (e.eventId && (e.code === "still_open" || e.code === "no_such_event")) {
      this.client.listInbox(); // the computer disagrees with what we show: take its word for it
    } else if (e.code === "no_such_device" && this.revoking) {
      clearTimeout(this.revoking.timer);
      this.revoking.reject(errOf(e));
      this.revoking = null;
    }
  }

  private clearPending(err: Error): void {
    this.powerAsked = false;
    this.voiceAsked = false;
    this.rulesAsked = false;
    this.usageAsked = false;
    if (this.snap.usageLoading) this.set({ usageLoading: false });
    for (const [rid, c] of [...this.voiceCalls]) {
      this.voiceCalls.delete(rid);
      clearTimeout(c.timer);
      c.reject(err);
    }
    if (this.wakeCall) {
      clearTimeout(this.wakeCall.timer);
      this.wakeCall.resolve({ ok: false, message: err.message });
      this.wakeCall = null;
    }
    for (const p of [this.creating, this.revoking]) {
      if (p) {
        clearTimeout(p.timer);
        p.reject(err);
      }
    }
    this.creating = null;
    this.revoking = null;
    for (const [rid, f] of [...this.findCalls]) {
      this.findCalls.delete(rid);
      clearTimeout(f.timer);
      f.reject(err);
    }
    for (const [cwd, h] of [...this.historyRequests]) {
      this.historyRequests.delete(cwd);
      clearTimeout(h.timer);
      h.reject(err);
    }
    for (const [id, t] of [...this.transcriptRequests]) {
      this.transcriptRequests.delete(id);
      clearTimeout(t.timer);
      t.reject(err);
    }
    for (const [id, d] of [...this.diffRequests]) {
      this.diffRequests.delete(id);
      clearTimeout(d.timer);
      d.reject(err);
    }
    for (const [id, c] of [...this.gitCalls]) {
      this.gitCalls.delete(id);
      clearTimeout(c.timer);
      c.reject(err);
    }
    for (const p of this.folderRequests.splice(0)) {
      clearTimeout(p.timer);
      p.reject(err);
    }
    for (const p of this.responding.values()) {
      clearTimeout(p.timer);
      p.reject(err);
    }
    this.responding.clear();
  }
}

const diffKey = (sessionId: string, against?: string) => `${sessionId}\0${against ?? ""}`;
const sort = (sessions: SessionInfo[]) => [...sessions].sort((a, b) => b.startedAt - a.startedAt);
